import { ZonedDateTimeFactory } from '@csp/csp-common-date-util';
import { ApiOptions, CspError, CspErrorType, Maybe, User } from '@csp/csp-common-model';
import { filterMap, isDefined } from '@csp/csp-common-util';
import {
  AnsweredConsentStatusTypeV1,
  AnsweredConsentsV1,
  ApprovedConsentsV1,
  ArtifactConsentDocumentV1,
  ConsentFeatureTypeV1,
  ConsentFormV1,
  ConsentOnFeatureV1,
  ConsentTypeV1,
  PendingConsentsV1,
  ScheduledConsentsGenericContentV1,
  ScheduledConsentsV1,
} from '@csp/dto';
import { CspLogger } from '@csp/csp-be-util';
import {
  AppPatientConsentScreenSystemTextsBlock,
  WebPatientConsentScreenSystemTextsBlock,
} from '@csp/csp-pod-patient-content-definitions';
import { isEmpty } from 'lodash';
import { ConsentActionV1, ConsentArtifactV1 } from '@csp/dmdp-api-consent-dto';
import {
  AnsweredConsentDeprecated,
  AnsweredConsentRequest,
  AnsweredLegacyConsent,
  isLegacyConsent,
  SkippedConsent,
} from '../model/AnsweredConsent';
import { ConsentArtifact } from '../model/ConsentArtifact';
import { ConsentConfigOnFeatureV1, ConsentFeatureType } from '../model/ConsentConfigOnFeatureV1';
import { ConsentRequest } from '../model/ConsentRequest';
import { ConsentArtifactService } from '../service/ConsentArtifactService';
import { consentFilterUtil } from '../util/consentFilterUtil';
import { ScheduledConsentWithDocument } from '../model/ScheduledConsentWithDocument';
import { ConsentActionWithArtifactMeta } from '../model/ConsentAction';
import { ConsentPlatform } from '../model/ConsentPlatform';
import { CmsConsentDocument } from '../model/CmsConsentDocument';
import { DEFAULT_SCHEDULE_META_PROPERTY_ENABLE_RECONSENT } from './defaultMetaProperties';

export const fromConsentResponse = (
  mappedPendingConsents: ConsentFormV1[],
  systemTexts: AppPatientConsentScreenSystemTextsBlock,
): PendingConsentsV1 => ({
  id: 'getPendingConsents',
  pendingConsents: mappedPendingConsents,
  systemTexts: {
    acceptButton: systemTexts.button.accept,
    declineAlert: {
      title: systemTexts.header.declineAlert.text,
      closeButton: systemTexts.button.closeDeclineAlert,
    },
    declineButton: systemTexts.button.decline,
    declineConfirmationModal: {
      title: systemTexts.header.nonReconsentDeclineConfirmation.text,
      body: systemTexts.body.declineAlertNonMandatoryConsent.text,
      nonReconsentWarning: systemTexts.body.nonReconsentDeclineConfirmation.text,
      consentSubmissionFailedHeader: systemTexts.header.consentSubmissionFailed.text,
      consentSubmissionFailedBody: systemTexts.body.consentSubmissionFailed.text,
      tryLaterButton: systemTexts.button.tryLater,
    },
    nonReconsentDeclineWarning: systemTexts.textLabel.nonReconsentDeclineWarning.label,
    versionLabel: systemTexts.textLabel.version.label,
  },
});

export const fromApprovedConsentResponse = (mappedApprovedConsents: ConsentFormV1[]): ApprovedConsentsV1 => ({
  id: 'getApprovedConsents',
  approvedConsents: mappedApprovedConsents.map((item: ConsentFormV1) => ({
    document: item.document,
    artifact: item.artifact,
    config: item.config,
    id: item.id,
  })),
});

interface MapConsentsWithArtifactsParameters {
  activeAppConsents: ConsentRequest[];
  artifacts: ConsentArtifact[];
  cmsContents: CmsConsentDocument[];
  logger?: CspLogger;
}

export const mapConsentsWithArtifacts = ({
  activeAppConsents,
  artifacts,
  cmsContents,
  logger,
}: MapConsentsWithArtifactsParameters): ConsentFormV1[] =>
  activeAppConsents.map((request: ConsentRequest) => {
    if (!request.details) {
      throw new CspError(CspErrorType.BAD_STATE, `Details must be set. RequestCode: ${request.requestCode}`);
    }

    const artifact = getArtifact(artifacts, request);
    const { mandatory, enableReconsent = DEFAULT_SCHEDULE_META_PROPERTY_ENABLE_RECONSENT } = request.meta;

    if (!('enableReconsent' in request.meta)) {
      logger?.warning(
        `missing property "request.meta.enableReconsent" for request of artifact: "${artifact?.artifactCode}", defaulting to: "${enableReconsent}"`,
      );
    }

    const document = ConsentArtifactService.getArtifactCmsDocument(artifact.config.cmsConsentType, cmsContents);

    return {
      id: request.details.artifactCode,
      consentType: ConsentTypeV1.CONSENT_FORM_V1,
      artifact: {
        artifactCode: request.details.artifactCode,
        documentReference: artifact.documentReference,
      },
      document: getEmptyArtifactConsentDocument(document),
      config: {
        mandatory,
        onFeature: getFeatures(request),
        enableReconsent,
      },
    };
  });

const getArtifact = (artifacts: ConsentArtifact[], request: ConsentRequest): ConsentArtifact => {
  const artifact = artifacts.find(artifact => artifact.artifactCode === request.details?.artifactCode);

  // This shouldn't be possible since the artifacts retrieved by consent.artifactCode previously
  if (!artifact) {
    throw CspError.badState(`${request.details?.artifactCode} was not found in the artifacts list`);
  }

  return artifact;
};

const getFeatures = (request: ConsentRequest): ConsentOnFeatureV1[] => {
  const features: ConsentOnFeatureV1[] = request.meta.onFeature.map((feature: ConsentConfigOnFeatureV1) => {
    switch (feature.type) {
      case ConsentFeatureType.ON_ONBOARDING:
        return {
          type: ConsentFeatureTypeV1.ON_ONBOARDING,
        };
      case ConsentFeatureType.TELEHEALTH:
        return {
          type: ConsentFeatureTypeV1.TELEHEALTH,
        };
      case ConsentFeatureType.ANALYTICS:
        return {
          type: ConsentFeatureTypeV1.ANALYTICS,
        };
      default:
        throw CspError.badState('No valid feature types found'); // TODO: fix
    }
  });

  return features;
};

export const getEmptyArtifactConsentDocument = (consentDocument: CmsConsentDocument): ArtifactConsentDocumentV1 => ({
  ...consentDocument,
  acceptDialogHeader: '',
  declineConfirmationDialogHeader: '',
  withdrawConfirmationDialogHeader: '',
  withdrawDataProcessingOptionsDialogBody: '',
  withdrawDataProcessingOptionsDialogRadioLabel: '',
  withdrawDataProcessingOptionsDialogContinueUsingData: {
    accessibilityHint: '',
    accessibilityLabel: '',
    label: '',
  },
  withdrawDataProcessingOptionsDialogStopUsingData: {
    accessibilityHint: '',
    accessibilityLabel: '',
    label: '',
  },
});

interface FromRequestsToSortedAnsweredConsentsParams {
  requests: ConsentRequest[];
  skippedConsents: SkippedConsent[];
  user: Pick<User, 'userId'>;
  artifacts: ConsentArtifact[];
  cmsContents: CmsConsentDocument[];
  apiOptions?: ApiOptions;
  logger?: CspLogger;
}

export const fromRequestsToSortedAnsweredConsents = async ({
  requests,
  skippedConsents = [],
  user,
  artifacts,
  cmsContents,
  apiOptions,
}: FromRequestsToSortedAnsweredConsentsParams): Promise<AnsweredConsentDeprecated[]> => {
  if (isEmpty(requests)) {
    return [];
  }

  const answeredRequests = await consentFilterUtil.filterAnsweredRequests(requests, skippedConsents, user, apiOptions);

  return answeredRequests
    .map((request): Maybe<AnsweredConsentDeprecated> => {
      const artifact = artifacts.find(artifact => artifact.artifactCode === request.details?.artifactCode);
      const document = artifact
        ? ConsentArtifactService.getArtifactCmsDocument(artifact.config.cmsConsentType, cmsContents)
        : null;

      return artifact && document
        ? {
            id: artifact.artifactId,
            artifact: {
              artifactCode: artifact.artifactCode,
              documentReference: artifact.documentReference,
            },
            approvalId: request.approval?.approvalId,
            consentStatus: getConsentStatus(request),
            document: {
              acceptDialogHeader: '',
              declineConfirmationDialogHeader: '',
              withdrawConfirmationDialogHeader: '',
              withdrawDataProcessingOptionsDialogBody: '',
              withdrawDataProcessingOptionsDialogRadioLabel: '',
              withdrawDataProcessingOptionsDialogContinueUsingData: {
                accessibilityHint: '',
                accessibilityLabel: '',
                label: '',
              },
              withdrawDataProcessingOptionsDialogStopUsingData: {
                accessibilityHint: '',
                accessibilityLabel: '',
                label: '',
              },
              ...document,
            },
            mandatory: request.meta.mandatory,
          }
        : undefined;
    })
    .filter(isDefined)
    .sort((a, b) => Number(b.mandatory) - Number(a.mandatory));
};

interface FromLegacyConsentsToAnsweredConsentsParams {
  legacyConsents: ConsentFormV1[];
  user: Pick<User, 'userId'>;
  apiOptions?: ApiOptions;
}

export const fromLegacyConsentsToAnsweredConsents = async ({
  legacyConsents,
  user,
  apiOptions,
}: FromLegacyConsentsToAnsweredConsentsParams): Promise<AnsweredConsentDeprecated[]> => {
  const legacyConsentsWithApprovals = await consentFilterUtil.filterAnsweredLegacyConsents(
    legacyConsents,
    user,
    apiOptions,
  );

  return legacyConsentsWithApprovals.map(legacyConsent => ({
    id: legacyConsent.id,
    artifact: legacyConsent.artifact,
    consentStatus: getConsentStatus(legacyConsent),
    document: legacyConsent.document,
    mandatory: legacyConsent.config.mandatory,
    approvalId: legacyConsent.approval?.approvalId,
  }));
};

export const parseStringifiedDate = (date: string): Date => {
  const parsedDate = ZonedDateTimeFactory.fromZonedDateTimeString(date);

  if (!parsedDate) {
    throw CspError.badState(`unable to parse date: "${date}"`);
  }

  return new Date(parsedDate.unixTimeMillis);
};

const getConsentStatus = (
  consent: AnsweredConsentRequest | AnsweredLegacyConsent,
): AnsweredConsentDeprecated['consentStatus'] => {
  if (isLegacyConsent(consent) && consent.approval) {
    return {
      status: AnsweredConsentStatusTypeV1.APPROVED,
      statusUpdateTimestamp: parseStringifiedDate(consent.approval?.approvalTimestamp).toISOString(),
    };
  }

  if (!isLegacyConsent(consent)) {
    if (consent.approval) {
      // regular consents will have an approval property if they have been accepted at least once
      return {
        status: consent.approval.withdrawn
          ? AnsweredConsentStatusTypeV1.DECLINED
          : AnsweredConsentStatusTypeV1.APPROVED,
        statusUpdateTimestamp: consent.approval.withdrawn
          ? consent.approval.withdrawn.withdrawnAt
            ? parseStringifiedDate(consent.approval.withdrawn.withdrawnAt).toISOString()
            : ''
          : parseStringifiedDate(consent.approval.approvalTimestamp).toISOString(),
      };
    } else if ('skippedAt' in consent) {
      // regular consents without approval should be optional consents which users have declined (never accepted)
      return {
        status: AnsweredConsentStatusTypeV1.DECLINED,
        statusUpdateTimestamp: consent.skippedAt ? consent.skippedAt : '',
      };
    }
  }

  const artifactCode =
    (isLegacyConsent(consent)
      ? consent.artifact.artifactCode
      : (consent as AnsweredConsentRequest).details?.artifactCode) ?? 'N/A';
  throw CspError.badState(`unable to determine state of consent: "${artifactCode}"`);
};

export const fromAnsweredConsentsResponse = (
  consents: AnsweredConsentDeprecated[],
  content: AppPatientConsentScreenSystemTextsBlock,
): AnsweredConsentsV1 => ({
  id: 'getAllAnsweredConsents',
  consents,
  content: {
    acceptButton: {
      label: content.button.accept.label,
      accessibilityLabel: content.button.accept.accessibilityLabel,
      accessibilityHint: content.button.accept.accessibilityHint,
    },
    declineButton: {
      label: content.button.decline.label,
      accessibilityLabel: content.button.decline.accessibilityLabel,
      accessibilityHint: content.button.decline.accessibilityHint,
    },
    closeDeclineAlertButton: {
      label: content.button.closeDeclineAlert.label,
      accessibilityLabel: content.button.closeDeclineAlert.accessibilityLabel,
      accessibilityHint: content.button.closeDeclineAlert.accessibilityHint,
    },
    withdrawButton: {
      label: content.button.withdraw.label,
      accessibilityLabel: content.button.withdraw.accessibilityLabel,
      accessibilityHint: content.button.withdraw.accessibilityHint,
    },
    withdrawAlertYesButton: {
      label: content.button.yesButton.label,
      accessibilityLabel: content.button.yesButton.accessibilityLabel,
      accessibilityHint: content.button.yesButton.accessibilityHint,
    },
    withdrawAlertNoButton: {
      label: content.button.noButton.label,
      accessibilityLabel: content.button.noButton.accessibilityLabel,
      accessibilityHint: content.button.noButton.accessibilityHint,
    },
    changeConsentButton: {
      label: content.button.changeConsent.label,
      accessibilityLabel: content.button.changeConsent.accessibilityLabel,
      accessibilityHint: content.button.changeConsent.accessibilityHint,
    },
    acceptedLabel: content.textLabel.accepted.label,
    acceptedDate: content.textLabel.acceptedDate.label,
    acceptedTodayLabel: content.textLabel.acceptedDateToday.label,
    acceptedYesterdayLabel: content.textLabel.acceptedDateYesterday.label,
    declinedLabel: content.textLabel.declined.label,
    declinedDate: content.textLabel.declinedDate.label,
    declinedTodayLabel: content.textLabel.declinedDateToday.label,
    declinedYesterdayLabel: content.textLabel.declinedDateYesterday.label,
    withdrawAlertHeader: content.header.withdrawAlert.label,
    withdrawAlertBody: content.body.withdrawAlert.label,
  },
});

export type AppWebSystemTextIntersection = AppPatientConsentScreenSystemTextsBlock &
  WebPatientConsentScreenSystemTextsBlock;

export const isAppPatientContent = (
  _block: AppPatientConsentScreenSystemTextsBlock | WebPatientConsentScreenSystemTextsBlock,
  platform: ConsentPlatform,
): _block is AppPatientConsentScreenSystemTextsBlock => platform === ConsentPlatform.PATIENT_APP;

export const toWebAppSystemTextIntersection = (
  block: AppPatientConsentScreenSystemTextsBlock | WebPatientConsentScreenSystemTextsBlock,
  platform: ConsentPlatform,
): AppWebSystemTextIntersection => {
  const shared = {
    button: {
      accept: block.button.accept,
      decline: block.button.decline,
      withdraw: block.button.withdraw,
      closeDeclineAlert: block.button.closeDeclineAlert,
      readMore: block.button.readMore,
      changeConsent: block.button.changeConsent,
      review: block.button.review,
      stopUseCollectedData: block.button.stopUseCollectedData,
      tryLater: block.button.tryLater,
      withdrawalConfirmation: block.button.withdrawalConfirmation,
      withdrawalDecline: block.button.withdrawalDecline,
      close: block.button.close,
      startWithdrawal: block.button.startWithdrawal,
      declineCancel: block.button.declineCancel,
    },
    header: {
      declineAlert: block.header.declineAlert,
      withdrawAlert: block.header.withdrawAlert,
      nonReconsentDeclineConfirmation: block.header.nonReconsentDeclineConfirmation,
      consentSubmissionFailed: block.header.consentSubmissionFailed,
      acceptAnalytics: block.header.acceptAnalytics,
      withdrawAnalytics: block.header.withdrawAnalytics,
      declineAnalytics: block.header.declineAnalytics,
      acceptSpfq: block.header.acceptSpfq,
      withdrawSpfq: block.header.withdrawSpfq,
      declineSpfq: block.header.declineSpfq,
      acceptGeneric: block.header.acceptGeneric,
      withdrawGeneric: block.header.withdrawGeneric,
      declineGeneric: block.header.declineGeneric,
    },
    body: {
      declineAlert: block.body.declineAlert,
      withdrawAlert: block.body.withdrawAlert,
      declineAlertNonMandatoryConsent: block.body.declineAlertNonMandatoryConsent,
      nonReconsentDeclineConfirmation: block.body.nonReconsentDeclineConfirmation,
      nonReconsentWithdrawalConfirmation: block.body.nonReconsentWithdrawalConfirmation,
      consentSubmissionFailed: block.body.consentSubmissionFailed,
      analyticsWithdrawalModal: block.body.analyticsWithdrawalModal,
      spfqWithdrawalModal: block.body.spfqWithdrawalModal,
      genericWithdrawalModal: block.body.genericWithdrawalModal,
    },
    textLabel: {
      acceptedDate: block.textLabel.acceptedDate,
      acceptedDateToday: block.textLabel.acceptedDateToday,
      acceptedDateYesterday: block.textLabel.acceptedDateYesterday,
      declinedDate: block.textLabel.declinedDate,
      declinedDateToday: block.textLabel.declinedDateToday,
      declinedDateYesterday: block.textLabel.declinedDateYesterday,
      declined: block.textLabel.declined,
      accepted: block.textLabel.accepted,
      nonReconsentDeclineWarning: block.textLabel.nonReconsentDeclineWarning,
      version: block.textLabel.version,
      pending: block.textLabel.pending,
      nonReconsentWithdrawalWarning: block.textLabel.nonReconsentWithdrawalWarning,
      declinedAndDeleteData: block.textLabel.declinedAndDeleteData,
      declinedAndDeleteDataDate: block.textLabel.declinedAndDeleteDataDate,
      declinedAndContinueUsingDataDate: block.textLabel.declinedAndContinueUsingDataDate,
    },
    form: block.form,
  };
  if (isAppPatientContent(block, platform)) {
    return {
      ...shared,
      button: {
        ...shared.button,
        yesButton: block.button.yesButton,
        noButton: block.button.noButton,
        deleteData: block.button.deleteData,
      },
      header: {
        ...shared.header,
        acceptUmux: block.header.acceptUmux,
        withdrawUmux: block.header.withdrawUmux,
        declineUmux: block.header.declineUmux,
        acceptCsat: block.header.acceptCsat,
        withdrawCsat: block.header.withdrawCsat,
        declineCsat: block.header.declineCsat,
      },
      body: {
        ...shared.body,
        umuxWithdrawalModal: block.body.umuxWithdrawalModal,
        csatWithdrawalModal: block.body.csatWithdrawalModal,
      },
      textLabel: {
        ...shared.textLabel,
        errorMessage: block.textLabel.errorMessage,
      },
    };
  } else {
    return {
      ...shared,
      button: {
        ...shared.button,
        yesButton: { label: '', accessibilityLabel: '', accessibilityHint: '' },
        noButton: { label: '', accessibilityLabel: '', accessibilityHint: '' },
        deleteData: { label: '', accessibilityLabel: '', accessibilityHint: '' },
      },
      header: {
        ...shared.header,
        acceptUmux: { text: '' },
        withdrawUmux: { text: '' },
        declineUmux: { text: '' },
        acceptCsat: { text: '' },
        withdrawCsat: { text: '' },
        declineCsat: { text: '' },
      },
      body: {
        ...shared.body,
        umuxWithdrawalModal: { markdown: '' },
        csatWithdrawalModal: { markdown: '' },
      },
      textLabel: {
        ...shared.textLabel,
        errorMessage: { label: '' },
      },
    };
  }
};

export const toScheduledConsentsGenericContentV1 = (
  content: AppWebSystemTextIntersection,
): ScheduledConsentsGenericContentV1 => ({
  acceptButton: {
    accessibilityHint: content.button.accept.accessibilityHint,
    accessibilityLabel: content.button.accept.accessibilityLabel,
    label: content.button.accept.label,
  },
  withdrawButton: {
    accessibilityHint: content.button.withdraw.accessibilityHint,
    accessibilityLabel: content.button.withdraw.accessibilityLabel,
    label: content.button.withdraw.label,
  },
  acceptDialog: {
    header: content.header.acceptGeneric.label,
    headerAnalytics: content.header.acceptAnalytics.label,
    headerSPFQ: content.header.acceptSpfq.label,
    errorMessage: content.body.consentSubmissionFailed.text,
  },
  acceptedLabel: content.textLabel.accepted.label,
  acceptedDate: content.textLabel.acceptedDate.label,
  acceptedTodayLabel: content.textLabel.acceptedDateToday.label,
  acceptedYesterdayLabel: content.textLabel.acceptedDateYesterday.label,
  changeConsentButton: {
    accessibilityHint: content.button.changeConsent.accessibilityHint,
    accessibilityLabel: content.button.changeConsent.accessibilityLabel,
    label: content.button.changeConsent.label,
  },
  declineAlert: {
    closeButton: {
      accessibilityHint: content.button.closeDeclineAlert.accessibilityHint,
      accessibilityLabel: content.button.closeDeclineAlert.accessibilityLabel,
      label: content.button.closeDeclineAlert.label,
    },
    title: content.header.declineAlert.text,
  },
  declineConfirmationDialog: {
    header: content.header.declineGeneric.label,
    headerAnalytics: content.header.declineAnalytics.label,
    headerSPFQ: content.header.declineSpfq.label,
    body: content.body.declineAlertNonMandatoryConsent.text,
    errorMessage: content.body.consentSubmissionFailed.text,
    nonReconsentWarning: content.body.nonReconsentDeclineConfirmation.text,
    cancelButton: {
      accessibilityHint: content.button.declineCancel.accessibilityHint,
      accessibilityLabel: content.button.declineCancel.accessibilityLabel,
      label: content.button.declineCancel.label,
    },
    confirmButton: {
      accessibilityHint: content.button.decline.accessibilityHint,
      accessibilityLabel: content.button.decline.accessibilityLabel,
      label: content.button.decline.label,
    },
  },
  declineButton: {
    accessibilityHint: content.button.decline.accessibilityHint,
    accessibilityLabel: content.button.decline.accessibilityLabel,
    label: content.button.decline.label,
  },
  declinedLabel: content.textLabel.declined.label,
  declinedDate: content.textLabel.declinedDate.label,
  declinedTodayLabel: content.textLabel.declinedDateToday.label,
  declinedYesterdayLabel: content.textLabel.declinedDateYesterday.label,
  declinedAndContinueUsingDataDate: content.textLabel.declinedAndContinueUsingDataDate.label,
  declinedAndDeleteDataDate: content.textLabel.declinedAndDeleteDataDate.label,
  nonReconsentDeclineWarning: content.textLabel.nonReconsentDeclineWarning.label,
  nonReconsentWithdrawalWarning: content.textLabel.nonReconsentWithdrawalWarning.label,
  pendingReviewLabel: content.textLabel.pending.label,
  reviewConsentButton: {
    label: content.button.review.label,
    accessibilityLabel: content.button.review.accessibilityLabel,
    accessibilityHint: content.button.review.accessibilityHint,
  },
  startWithdrawalButton: {
    accessibilityHint: content.button.startWithdrawal.accessibilityHint,
    accessibilityLabel: content.button.startWithdrawal.accessibilityLabel,
    label: content.button.startWithdrawal.label,
  },
  stopUseCollectedDataButton: {
    accessibilityHint: content.button.stopUseCollectedData.accessibilityHint,
    accessibilityLabel: content.button.stopUseCollectedData.accessibilityLabel,
    label: content.button.stopUseCollectedData.label,
  },
  tryLaterButton: {
    accessibilityHint: content.button.tryLater.accessibilityHint,
    accessibilityLabel: content.button.tryLater.accessibilityLabel,
    label: content.button.tryLater.label,
  },
  versionLabel: content.textLabel.version.label,
  withdrawConfirmationDialog: {
    header: content.header.withdrawAlert.label,
    headerAnalytics: content.header.withdrawAnalytics.label,
    headerSPFQ: content.header.withdrawSpfq.label,
    body: content.body.withdrawAlert.label,
    nonReconsentWarning: content.body.nonReconsentWithdrawalConfirmation.text,
    errorMessage: content.body.consentSubmissionFailed.text,
    cancelButton: {
      label: content.button.withdrawalDecline.label,
      accessibilityLabel: content.button.withdrawalDecline.accessibilityLabel,
      accessibilityHint: content.button.withdrawalDecline.accessibilityHint,
    },
    confirmButton: {
      label: content.button.withdrawalConfirmation.label,
      accessibilityLabel: content.button.withdrawalConfirmation.accessibilityLabel,
      accessibilityHint: content.button.withdrawalConfirmation.accessibilityHint,
    },
  },
  withdrawDataProcessingOptionsDialog: {
    header: content.header.withdrawAlert.label,
    headerAnalytics: content.header.withdrawAnalytics.label,
    headerSPFQ: content.header.withdrawSpfq.label,
    body: content.body.genericWithdrawalModal.markdown,
    bodyAnalytics: content.body.analyticsWithdrawalModal.markdown,
    bodySPFQ: content.body.spfqWithdrawalModal.markdown,
    radioGroupLabel: content.form.radioButton.header.label,
    radioLabelContinueUsingCollectedData: {
      accessibilityHint: content.form.radioButton.continueUsingData.accessibilityHint,
      accessibilityLabel: content.form.radioButton.continueUsingData.accessibilityLabel,
      label: content.form.radioButton.continueUsingData.label,
    },
    radioLabelStopUsingCollectedData: {
      accessibilityHint: content.form.radioButton.stopUsingData.accessibilityHint,
      accessibilityLabel: content.form.radioButton.stopUsingData.accessibilityLabel,
      label: content.form.radioButton.stopUsingData.label,
    },
    cancelButton: {
      accessibilityHint: content.button.withdrawalDecline.accessibilityHint,
      accessibilityLabel: content.button.withdrawalDecline.accessibilityLabel,
      label: content.button.withdrawalDecline.label,
    },
    closeButton: {
      accessibilityHint: content.button.close.accessibilityHint,
      accessibilityLabel: content.button.close.accessibilityLabel,
      label: content.button.close.label,
    },
    confirmButton: {
      accessibilityHint: content.button.withdrawalConfirmation.accessibilityHint,
      accessibilityLabel: content.button.withdrawalConfirmation.accessibilityLabel,
      label: content.button.withdrawalConfirmation.label,
    },
  },
  // The next properties are deprecated in graphql typedefs, kept for backward-compatibility until they can be removed (11.0?)
  closeDeclineAlertButton: {
    accessibilityHint: content.button.closeDeclineAlert.accessibilityHint,
    accessibilityLabel: content.button.closeDeclineAlert.accessibilityLabel,
    label: content.button.closeDeclineAlert.label,
  },
  consentSubmissionFailedHeader: content.header.consentSubmissionFailed.text,
  consentSubmissionFailedBody: content.body.consentSubmissionFailed.text,
  withdrawAlertHeader: content.header.withdrawAlert.label,
  withdrawAlertBody: content.body.withdrawAlert.label,
  withdrawAlertNonReconsentBody: content.body.nonReconsentWithdrawalConfirmation.text,
  withdrawAlertNoButton: {
    label: content.button.noButton.label,
    accessibilityLabel: content.button.noButton.accessibilityLabel,
    accessibilityHint: content.button.noButton.accessibilityHint,
  },
  withdrawAlertYesButton: {
    accessibilityHint: content.button.yesButton.accessibilityHint,
    accessibilityLabel: content.button.yesButton.accessibilityLabel,
    label: content.button.yesButton.label,
  },
  withdrawAlertYesAndDeleteDataButton: {
    accessibilityHint: content.button.deleteData.accessibilityHint,
    accessibilityLabel: content.button.deleteData.accessibilityLabel,
    label: content.button.deleteData.label,
  },
});

export const toGetScheduledConsentsResponse = (
  consents: ScheduledConsentWithDocument[],
  content: AppPatientConsentScreenSystemTextsBlock,
): ScheduledConsentsV1 => ({
  id: 'getScheduledConsents',
  consents,
  stepByStepTrainings: [],
  questionnaireTrainings: [],
  content: toScheduledConsentsGenericContentV1(content),
});

export const toGetUnresolvedConsentsResponse = (
  consents: Omit<ScheduledConsentWithDocument, 'country' | 'documentClientType'>[],
  content: ScheduledConsentsGenericContentV1,
): ScheduledConsentsV1 => ({
  id: 'getUnresolvedConsents',
  consents,
  stepByStepTrainings: [],
  questionnaireTrainings: [],
  content,
});

export const mapConsentActionV1WithArtifactMeta = (
  consentActionV1s: Array<ConsentActionV1>,
  artifactsByArtifactCode: Record<string, ConsentArtifactV1>,
  consentClientType: ConsentPlatform,
): Array<ConsentActionWithArtifactMeta> =>
  filterMap<ConsentActionV1, ConsentActionWithArtifactMeta>(consentActionV1s, (consentAction: ConsentActionV1) => {
    const matchingArtifact = artifactsByArtifactCode[consentAction.artifactCode];
    const { CONFIG_V2 } = matchingArtifact?.meta ?? {};
    const { clientType, country, version, cmsConsentType } = CONFIG_V2 ?? {};

    if (clientType === consentClientType && country && version && cmsConsentType) {
      return {
        ...consentAction,
        artifactMeta: {
          CONFIG_V2: {
            clientType,
            country,
            version,
            cmsConsentType,
          },
        },
      };
    } else {
      return undefined;
    }
  });
